import { useState } from "react";
import { Hash, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const OPTIONS = [
  { value: "planned", label: "Ej startad", hint: "Ordern blir aktiv igen, ingen tid är startad" },
  { value: "in_progress", label: "Pågående", hint: "Ordern blir aktiv igen med registrerad tid kvar" },
  { value: "ready_to_invoice", label: "Avslutat", hint: "Arbetet är klart och ordern är redo att faktureras" },
];

export default function StatusChangeButton({ job, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const change = async (status) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/status`, { status });
      toast.success(`Status ändrad till ${OPTIONS.find((o) => o.value === status)?.label}`);
      setOpen(false);
      await onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="status-change-hash-button"
        title="Ändra status på arbetsordern"
        onClick={() => setOpen(true)}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 transition-colors duration-200 hover:border-slate-900 hover:bg-slate-900 hover:text-white"
      >
        <Hash className="h-4 w-4" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="status-change-dialog">
          <DialogHeader>
            <DialogTitle>Ändra status</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Flytta tillbaka en avslutad arbetsorder till aktiv. Registrerad tid, material och
            historik påverkas inte – inget raderas.
          </p>
          {job.invoiced && (
            <p className="rounded-md border border-indigo-200 bg-indigo-50 p-2 text-sm text-indigo-900" data-testid="status-change-invoiced-note">
              Ordern är fakturerad. Ta bort fakturamarkeringen först för att kunna byta status.
            </p>
          )}
          <div className="mt-2 space-y-2">
            {OPTIONS.map((o) => {
              const current = job.status === o.value
                || (o.value === "ready_to_invoice" && job.status === "done");
              return (
                <Button
                  key={o.value}
                  data-testid={`status-change-${o.value}`}
                  variant="outline"
                  disabled={busy || current}
                  onClick={() => change(o.value)}
                  className={`h-auto w-full justify-start whitespace-normal py-2.5 text-left transition-colors duration-200 ${
                    current ? "border-slate-900 bg-slate-900 text-white" : "bg-white hover:bg-slate-100"
                  }`}
                >
                  <span className="flex flex-col items-start">
                    <span className="font-semibold">
                      {o.label}{current ? " (nuvarande)" : ""}
                    </span>
                    <span className={`text-xs ${current ? "text-slate-200" : "text-slate-500"}`}>{o.hint}</span>
                  </span>
                </Button>
              );
            })}
          </div>
          {busy && (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Sparar…
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
